import { inject, Injectable } from '@angular/core';

import { normalizeProvinceCode } from '../../../../../utils/province-code';
import { CustomerAddress } from '../../../../model/customer-information.model';
import { CustomerVehicleRelationType } from '../../../../model/customer-vehicle.model';
import { FoundCustomer } from '../../../../model/found-customer.model';
import {
  RetailCrmAccount,
  RetailCrmAccountsResponse,
  RetailCrmAddress,
  RetailCrmCustomerMainType,
} from '../../model/responses/get-accounts.model';
import {
  RetailCrmOwnerKeeperUserData,
  RetailCrmVehicle,
  RetailCrmVehiclesResponse,
} from '../../model/responses/vehicles.model';
import { RetailCrmCustomerFieldsViewModelMapper } from './customer-fields-model.mapper';

@Injectable({
  providedIn: 'root',
})
export class RetailCrmFoundCustomerViewModelMapper {
  private readonly customerFieldsMapper = inject(RetailCrmCustomerFieldsViewModelMapper);

  mapToFoundCustomers(accountsResponse: RetailCrmAccountsResponse): FoundCustomer[] {
    return accountsResponse.map((account) => this.mapAccountToFoundCustomer(account));
  }

  public mapVehiclesToFoundCustomers(vehicles: RetailCrmVehiclesResponse): FoundCustomer[] {
    // Map accountNumber -> foundCustomer to exclude duplicate customers based on accountNumber
    const customerMap = new Map<string, FoundCustomer>();

    // Add each customer to the map of found customers - but only once per accountNumber
    vehicles.forEach((vehicle) => {
      const foundCustomers = this.mapVehicleToFoundCustomers(vehicle);
      foundCustomers.forEach((customer) => {
        if (customer.accountNumber && !customerMap.has(customer.accountNumber)) {
          customerMap.set(customer.accountNumber, customer);
        }
      });
    });

    return Array.from(customerMap.values());
  }

  mapVehicleToFoundCustomers(vehicle: RetailCrmVehicle): FoundCustomer[] {
    const foundCustomers: FoundCustomer[] = [];

    for (const [customer, customerToVehicleRelation] of [
      [vehicle.owner, CustomerVehicleRelationType.OWNER],
      [vehicle.keeper, CustomerVehicleRelationType.KEEPER],
      [vehicle.user, CustomerVehicleRelationType.USER],
    ] as const) {
      // Retail CRM does also return empty customer, where all values are empty.
      // If accountNumber === undefined -> empty customer
      if (!customer?.accountNumber) {
        continue;
      }

      foundCustomers.push(this.mapOkuDataToFoundCustomer(customer, customerToVehicleRelation));
    }

    return foundCustomers;
  }

  mapVehicleRelationToFoundCustomer(
    vehicleRelation: RetailCrmOwnerKeeperUserData | undefined,
    relationType: CustomerVehicleRelationType,
  ): FoundCustomer | undefined {
    if (!vehicleRelation?.accountNumber) {
      return undefined;
    }

    return this.mapOkuDataToFoundCustomer(vehicleRelation, relationType);
  }

  private mapOkuDataToFoundCustomer(
    customer: RetailCrmOwnerKeeperUserData,
    customerToVehicleRelation: CustomerVehicleRelationType,
  ): FoundCustomer {
    return {
      gcid: customer.gcid,
      accountNumber: customer.accountNumber,
      bmwMiniId: customer.bmworMiniId,
      firstName: customer.firstName,
      middleName: customer.middleName,
      lastName: customer.lastName,
      suffix: customer.suffix,
      address: this.mapAddress(customer.privateAddress),
      email: customer.emailPrivate || customer.emailBusiness,
      phone: customer.phonePrivate || customer.phoneBusiness,
      mobile: customer.mobilePrivate || customer.mobileBusiness,
      isIndividual: customer.customerMainType === RetailCrmCustomerMainType.INDIVIDUAL,
      customerToVehicleRelations: new Set([customerToVehicleRelation]),
      taxNumber: customer.taxNumber,
      nationalId1: customer.nationalId1,
      nationalId1DocumentType: this.customerFieldsMapper.mapNationalIdDocumentType(customer.nationalId1DocumentType),
      inSyncWithDirectSales: customer.inSyncWithDirectSales,
    };
  }

  private mapAccountToFoundCustomer(data: RetailCrmAccount): FoundCustomer {
    return {
      gcid: data.gcid,
      accountNumber: data.accountNumber, // Indicates that the customer was loaded from Retail CRM
      bmwMiniId: data.bmwOrMiniId,
      firstName: data.name?.firstName,
      middleName: data.name?.middleName,
      lastName: data.name?.lastName,
      suffix: data.name?.suffix,
      phone: data?.connection?.phonePrivate || data?.connection?.phoneBusiness,
      mobile: data?.connection?.mobilePrivate || data?.connection?.mobileBusiness,
      email: data?.connection?.emailPrivate || data?.connection?.emailBusiness,
      address: this.mapAddress(data?.privateAddress || data?.businessAddress),
      isIndividual: data.customerMainType === RetailCrmCustomerMainType.INDIVIDUAL,
      taxNumber: data.taxNumber,
      nationalId1: data.nationalId1,
      nationalId1DocumentType: this.customerFieldsMapper.mapNationalIdDocumentType(data.nationalId1DocumentType),
      inSyncWithDirectSales: data.inSyncWithDirectSales,
    };
  }

  private mapAddress(retailCrmAddress?: RetailCrmAddress): CustomerAddress {
    return {
      houseNumber: retailCrmAddress?.addressStreetNumber,
      street: retailCrmAddress?.addressStreet,
      city: retailCrmAddress?.addressCity,
      postalCode: retailCrmAddress?.addressPostalCode,
      province: normalizeProvinceCode(retailCrmAddress?.addressState, retailCrmAddress?.addressCountry),
      country: retailCrmAddress?.addressCountry,
      addressLine1: retailCrmAddress?.addressComplement,
      addressLine2: retailCrmAddress?.addressLine2,
      postBox: retailCrmAddress?.addressPostBox,
    };
  }
}
